// ElementOrb + Glyph (§2) — the signature element visual.
// A glossy radial-gradient sphere tinted by the element color, with an animated
// SVG glyph inside representing what the element *is* (flickering flame, flowing
// water, forking lightning, spinning gear…). Ported from the prototype's
// orbStyle + buildGK + G(). Use this everywhere an element token appears.
import React from "react";
import { ELEMENT_DEFS } from "../elementDefinitions";

/* ------------------------------------------------------------- tier lookups */
export const TIER_RING: Record<number, string> = {
  1: "#9CA3AF",
  2: "#60A5FA",
  3: "#34D399",
  4: "#A78BFA",
  5: "#F472B6",
  6: "#FB923C",
  7: "#FBBF24",
};
export const TIER_NAME: Record<number, string> = {
  1: "Base",
  2: "Common",
  3: "Uncommon",
  4: "Rare",
  5: "Epic",
  6: "Legendary",
  7: "Mythic",
};
export const tierColor = (tier: number) => TIER_RING[tier] ?? TIER_RING[1];
export const tierName = (tier: number) => TIER_NAME[tier] ?? "Base";

/* ------------------------------------------------------------- glyph kinds */
export type GlyphKind =
  | "fire" | "water" | "drop" | "air" | "storm" | "bolt" | "ice" | "light"
  | "dark" | "earth" | "metal" | "gear" | "leaf" | "gem" | "human" | "chip"
  | "rocket" | "planet" | "star" | "flask" | "note" | "creature" | "spark";

// keyword → archetype map (buildGK). Every element id resolves to one of ~22
// glyph archetypes; anything unmapped falls back to `spark`.
const GK_GROUPS: Record<GlyphKind, string[]> = {
  fire: ["fire", "lava", "magma", "phoenix", "explosion", "volcano", "ash", "gunpowder"],
  water: ["water", "ocean", "tsunami", "mud", "swamp", "silt", "oasis", "geyser", "oil", "petroleum"],
  air: ["air", "smoke", "fog", "mist", "cloud", "dust", "steam"],
  storm: ["storm", "hurricane"],
  bolt: ["electricity", "lightning", "plasma", "energy", "battery", "generator", "light_bulb", "electronics"],
  ice: ["ice", "snow"],
  light: ["light", "aurora", "rainbow"],
  dark: ["dark", "shadow", "void"],
  earth: ["earth", "stone", "mountain", "sand", "desert", "clay", "brick", "pottery", "obsidian", "rust", "quicksand", "meteor", "fossil", "coal", "mudslide", "gravity", "pressure"],
  metal: ["metal", "steel", "magnet", "armor", "weapon", "tool", "metal_tool"],
  gear: ["machine", "factory", "robot", "wheel", "cart", "boat", "ship", "clock"],
  leaf: ["life", "plant", "tree", "forest", "flower", "moss", "algae", "wood", "farm", "coral", "life_form", "habitat"],
  gem: ["crystal", "diamond", "glass", "geode"],
  drop: ["rain", "acid"],
  human: ["human", "civilization", "city", "urban_city", "building", "cyborg", "war", "peace"],
  chip: ["computer", "ai", "internet", "logic", "quantum_computer", "virtual_reality", "nanobot", "bioengineering"],
  rocket: ["rocket", "satellite", "space_station", "space_colony", "terraforming", "fusion_reactor"],
  planet: ["planet", "moon", "galaxy", "multiverse", "space", "wormhole", "black_hole"],
  star: ["star", "aether", "spirit"],
  flask: ["medicine", "plastic"],
  creature: ["dragon", "bird", "fish", "lizard", "golem"],
  note: ["music", "art", "knowledge", "paper", "cloth"],
  spark: ["time"],
};

const GK_MAP: Record<string, GlyphKind> = (() => {
  const m: Record<string, GlyphKind> = {};
  (Object.keys(GK_GROUPS) as GlyphKind[]).forEach((kind) => {
    GK_GROUPS[kind].forEach((id) => (m[id] = kind));
  });
  return m;
})();

export const glyphKindFor = (id: string): GlyphKind => GK_MAP[id] ?? "spark";

/* -------------------------------------------------------------------- Glyph */
const W = "rgba(255,255,255,0.95)";
const D = "rgba(0,0,0,0.28)";

// animation style helper (A() from the prototype)
const anim = (name: string, dur: string, origin = "center"): React.CSSProperties => ({
  transformBox: "fill-box" as any,
  transformOrigin: origin,
  animation: `${name} ${dur} ease-in-out infinite`,
});

export interface GlyphProps {
  kind: GlyphKind;
  size?: number;
}

export const Glyph: React.FC<GlyphProps> = ({ kind, size = 26 }) => {
  const wrap = (children: React.ReactNode) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "block", overflow: "visible", filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.35))" }}
    >
      {children}
    </svg>
  );

  switch (kind) {
    case "fire":
      return wrap(<path d="M12 4c1.8 3 3.7 4.6 3.7 7.8A3.7 3.7 0 0 1 8.3 11.8c0-1.5.7-2.6 1.5-3.4.3 1.7 1.7 1.8 1.7.2 0-1.3-.4-2.7.5-4.6z" fill={W} style={anim("gFlicker", "0.85s", "12px 19px")} />);
    case "water":
      return wrap([0, 5, 10].map((dy, i) => (
        <path key={i} d={`M2.5 ${8 + dy}c1.9-2 3.8-2 5.7 0s3.8 2 5.7 0 3.8-2 5.7 0`} stroke={W} strokeWidth={1.7} strokeLinecap="round" style={anim("gFlow", `${2 + i * 0.35}s`)} />
      )));
    case "drop":
      return wrap(<path d="M12 4c4 5 6 8 6 11a6 6 0 0 1-12 0c0-3 2-6 6-11z" fill={W} style={anim("gRise", "2.6s")} />);
    case "air":
      return wrap([6, 12, 18].map((y, i) => (
        <path key={i} d={`M3 ${y}h10.5a2.4 2.4 0 1 0-2.4-2.4`} stroke={W} strokeWidth={1.7} strokeLinecap="round" style={anim("gDrift", `${2.2 + i * 0.4}s`)} />
      )));
    case "storm":
      return wrap([
        <path key={1} d="M6 13a4 4 0 0 1 1-7.9A5 5 0 0 1 17 6a3.4 3.4 0 0 1 .4 6.9" fill={W} opacity={0.9} />,
        <path key={2} d="M12 11l-2.4 4H12l-2 4.5" stroke="#FFEB3B" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" fill="none" style={anim("gFlash", "1s")} />,
      ]);
    case "bolt":
      return wrap(<path d="M13 2 5 13.5h4.6L8.5 22 18 9.5h-4.8z" fill={W} style={anim("gFlash", "1.1s")} />);
    case "ice":
      return wrap(<g style={anim("gSpin", "10s")}>{[0, 60, 120].map((r, i) => (
        <line key={i} x1={12} y1={2.5} x2={12} y2={21.5} stroke={W} strokeWidth={1.8} strokeLinecap="round" transform={`rotate(${r} 12 12)`} />
      ))}</g>);
    case "light":
      return wrap([
        <circle key="c" cx={12} cy={12} r={4.4} fill={W} style={anim("gShimmer", "2.4s")} />,
        <g key="r" style={anim("gSpin", "13s")}>{[0, 45, 90, 135, 180, 225, 270, 315].map((r, i) => (
          <line key={i} x1={12} y1={1.5} x2={12} y2={4.5} stroke={W} strokeWidth={1.8} strokeLinecap="round" transform={`rotate(${r} 12 12)`} />
        ))}</g>,
      ]);
    case "dark":
      return wrap(<path d="M16.5 3a9 9 0 1 0 0 18 7.2 7.2 0 0 1 0-18z" fill={W} style={anim("gShimmer", "3.4s")} />);
    case "earth":
      return wrap([
        <path key={1} d="M3 18.5h18l-5-8.5-3.2 4.5L10 11z" fill={W} style={anim("gShimmer", "4s")} />,
        <rect key={2} x={3} y={18.5} width={18} height={1.6} rx={0.8} fill={W} />,
      ]);
    case "metal":
      return wrap(<path d="M8 3.5h8l4 8.5-4 8.5H8l-4-8.5z" fill="none" stroke={W} strokeWidth={2} strokeLinejoin="round" style={anim("gShimmer", "3s")} />);
    case "gear":
      return wrap(<g style={anim("gSpin", "7s")}>
        <circle cx={12} cy={12} r={3.3} fill="none" stroke={W} strokeWidth={2} />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((r, i) => (
          <rect key={i} x={11} y={1.8} width={2} height={4} rx={0.6} fill={W} transform={`rotate(${r} 12 12)`} />
        ))}
      </g>);
    case "leaf":
      return wrap(<path d="M12 21C5.5 18 5 9.5 12 3.5c7 6 6.5 14.5 0 17.5zM12 8v11" stroke={W} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" fill="none" style={anim("gSway", "3.2s", "12px 21px")} />);
    case "gem":
      return wrap(<path d="M6 4h12l3 5-9 11.5L3 9zM6 4l6 16.5L18 4M3 9h18" stroke={W} strokeWidth={1.4} strokeLinejoin="round" fill="none" style={anim("gShimmer", "2.4s")} />);
    case "human":
      return wrap([
        <circle key={1} cx={12} cy={7} r={3.2} fill={W} />,
        <path key={2} d="M5 21c0-4 3.2-7 7-7s7 3 7 7" fill={W} />,
      ]);
    case "chip": {
      const pins = [9, 12, 15];
      const legs: React.ReactNode[] = [];
      pins.forEach((p, i) => {
        legs.push(<line key={`t${i}`} x1={p} y1={3.5} x2={p} y2={7} stroke={W} strokeWidth={1.5} strokeLinecap="round" />);
        legs.push(<line key={`b${i}`} x1={p} y1={17} x2={p} y2={20.5} stroke={W} strokeWidth={1.5} strokeLinecap="round" />);
        legs.push(<line key={`l${i}`} x1={3.5} y1={p} x2={7} y2={p} stroke={W} strokeWidth={1.5} strokeLinecap="round" />);
        legs.push(<line key={`r${i}`} x1={17} y1={p} x2={20.5} y2={p} stroke={W} strokeWidth={1.5} strokeLinecap="round" />);
      });
      return wrap([
        <rect key="o" x={7} y={7} width={10} height={10} rx={2} fill="none" stroke={W} strokeWidth={2} />,
        <rect key="i" x={10.3} y={10.3} width={3.4} height={3.4} rx={0.6} fill={W} style={anim("gShimmer", "1.4s")} />,
        ...legs,
      ]);
    }
    case "rocket":
      return wrap(<g style={anim("gRise", "2s")}>
        <path d="M12 2c3 2 4.2 6 4.2 9.2L14 14.5h-4L7.8 11.2C7.8 8 9 4 12 2z" fill={W} />
        <circle cx={12} cy={9} r={1.6} fill={D} />
        <path d="M9.5 15l-2 4.5 3-1.4M14.5 15l2 4.5-3-1.4" stroke={W} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </g>);
    case "planet":
      return wrap([
        <circle key={1} cx={12} cy={12} r={5.4} fill={W} />,
        <ellipse key={2} cx={12} cy={12} rx={10} ry={3.3} fill="none" stroke={W} strokeWidth={1.6} transform="rotate(-20 12 12)" style={anim("gShimmer", "3s")} />,
      ]);
    case "star":
      return wrap(<path d="M12 2l2.7 6.5 6.8.5-5.2 4.4 1.7 6.6L12 16.6 6 20.5l1.7-6.6L2.5 9.5l6.8-.5z" fill={W} style={anim("gTwinkle", "2s")} />);
    case "flask":
      return wrap([
        <path key={1} d="M10 3h4v5.2l3.9 8.6A2 2 0 0 1 16.1 20H7.9a2 2 0 0 1-1.8-3.2L10 8.2z" fill="none" stroke={W} strokeWidth={1.7} strokeLinejoin="round" />,
        <circle key={2} cx={10.6} cy={15} r={1} fill={W} style={anim("gRise", "1.6s")} />,
        <circle key={3} cx={13.6} cy={13} r={0.8} fill={W} style={anim("gRise", "2.1s")} />,
      ]);
    case "note":
      return wrap([
        <path key={1} d="M9 17.5V6l9-2v11" stroke={W} strokeWidth={1.8} fill="none" strokeLinecap="round" style={anim("gShimmer", "3s")} />,
        <circle key={2} cx={7} cy={17.5} r={2.2} fill={W} />,
        <circle key={3} cx={16} cy={15} r={2.2} fill={W} />,
      ]);
    case "creature":
      return wrap(<path d="M2.5 14.5c4.2-1 6.2-4.2 9.5-4.2s4.2 2.2 9.5 1.2c-3.2 3.2-6.2 4.2-9.5 4.2s-5.3 0-9.5-1.2z" fill={W} style={anim("gSway", "2.6s", "12px 12px")} />);
    case "spark":
    default:
      return wrap([
        <path key={1} d="M12 3v6M12 15v6M3 12h6M15 12h6" stroke={W} strokeWidth={1.8} strokeLinecap="round" style={anim("gTwinkle", "1.9s")} />,
        <circle key={2} cx={12} cy={12} r={2} fill={W} />,
      ]);
  }
};

/* --------------------------------------------------------------- ElementOrb */
export const orbGradient = (color: string): React.CSSProperties => ({
  background:
    "radial-gradient(circle at 34% 24%, rgba(255,255,255,0.42), rgba(255,255,255,0) 46%)," +
    "radial-gradient(circle at 50% 122%, rgba(0,0,0,0.3), rgba(0,0,0,0) 58%)," +
    color,
  boxShadow: `0 6px 16px ${color}55, inset 0 -7px 12px rgba(0,0,0,0.34), inset 0 3px 6px rgba(255,255,255,0.3)`,
});

export interface ElementOrbProps {
  /** Element id — color, tier and glyph are looked up from ELEMENT_DEFS. */
  id?: string;
  /** Explicit color/tier override (when rendering an element not in the store). */
  color?: string;
  tier?: number;
  size?: number;
  /** Show the tier-colored ring around the orb. */
  withRing?: boolean;
  /** Render the empty/locked placeholder orb instead. */
  locked?: boolean;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
}

const ElementOrb: React.FC<ElementOrbProps> = ({
  id,
  color,
  tier,
  size = 44,
  withRing = false,
  locked = false,
  className,
  style,
  title,
}) => {
  const def = id ? ELEMENT_DEFS[id] : undefined;
  const resolvedColor = color ?? def?.color ?? "#65A30D";
  const resolvedTier = tier ?? def?.tier ?? 1;

  if (locked) {
    return (
      <div
        className={className}
        title={title}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          flex: "0 0 auto",
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          boxShadow: "inset 0 -4px 8px rgba(0,0,0,0.35)",
          ...style,
        }}
      />
    );
  }

  const orb = (
    <div
      className={className}
      title={title ?? def?.name}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        flex: "0 0 auto",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...orbGradient(resolvedColor),
        ...style,
      }}
    >
      <Glyph kind={id ? glyphKindFor(id) : "spark"} size={Math.round(size * 0.58)} />
    </div>
  );

  if (!withRing) return orb;

  return (
    <div
      style={{
        padding: 3,
        borderRadius: "50%",
        border: `2px solid ${tierColor(resolvedTier)}`,
        display: "inline-flex",
      }}
    >
      {orb}
    </div>
  );
};

export default ElementOrb;
